<template>
  <div class="container">
    <div class="columns">
      <div class="column is-6">
        <LsSupportHeader :label="t('forms.oneTimeReschedule.header')" />
      </div>
    </div>
    <BLoading :active.sync="isLoading" />
    <div v-if="!isLoading" class="columns">
      <div class="column is-6">
        <form novalidate test-id="form" @submit.prevent="requestOTSDC">
          <LsSelect
            v-if="streetOptionsArray.length > 1"
            v-model="property_id"
            :validation="{ required: true }"
            :label="t('forms.oneTimeReschedule.propertySelector.label')"
            :placeholder="t('forms.oneTimeReschedule.propertySelector.placeholder')"
            track-id="one_time_reschedule_view-property_id"
            test-id="property_id"
            name="property_id"
            :options="streetOptionsArray"
            @input="propertyChanged"
          />
          <LsDatepicker
            v-model="date"
            :validation="{ required: true }"
            :label="t('forms.oneTimeReschedule.date')"
            track-id="one_time_reschedule_view-date"
            test-id="date"
            name="date"
            :min-date="minDate"
            :unselectable-days-of-week="unselectableDaysOfWeek"
          />
          <LsInfoCard>
            <p>
              {{
                t('forms.oneTimeReschedule.lookForNewPro', {
                  lookForNewPro: !requested_is_contractor_flexible ? t('not') : '',
                })
              }}
            </p>
          </LsInfoCard>
          <LsButton
            :loading="support.loadingStatus.isUpdating"
            :text="t('submitRequest')"
            class="submit-button"
            test-id="submit_request"
            bg-color="tertiary"
            track-id="one_time_reschedule_view-submit_request"
            type="submit"
          />
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { LsButton, LsSelect, LsDatepicker, LsInfoCard } from '@/components/common';
import _ from 'lodash';
import { mapActions, mapState, mapGetters } from 'vuex';
import { lsValidationService, lsToastService } from 'ls-vue-common';
import {
  PROPERTIES__PROPERTIES_IN_ARRAY_WITH_ACTIVE_MOWING,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  SUPPORT__DAY_OF_WEEK_AVAILABILITY,
  SUPPORT__ONE_TIME_RESCHEDULE,
} from '@/store/types';
import dayjs from 'dayjs';
import { LsSupportHeader } from '../components';
import { oneTimeServiceDayChangeReasons, DayToNumberMap } from 'ls-constants';

export default {
  name: 'OneTimeRescheduleView',
  components: {
    LsButton,
    LsSelect,
    LsDatepicker,
    LsSupportHeader,
    LsInfoCard,
  },
  data() {
    return {
      property_id: null,
      date: null,
      reason: null,
      reason_details: '',
      requested_is_contractor_flexible: true,
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  computed: {
    ...mapState(['support', 'properties']),
    ...mapGetters([PROPERTIES__PROPERTIES_IN_ARRAY_WITH_ACTIVE_MOWING]),
    streetOptionsArray() {
      return _.map(this[PROPERTIES__PROPERTIES_IN_ARRAY_WITH_ACTIVE_MOWING], (property) => {
        return {
          label: property.street,
          value: property.id,
        };
      });
    },
    oneTimeServiceDayChangeReasons() {
      return _.cloneDeep(oneTimeServiceDayChangeReasons);
    },
    isLoading() {
      return this.properties.loadingStatus.isLoading || this.support.loadingStatus.isLoading;
    },
    selectedProperty() {
      return this.properties.propertiesById[this.property_id];
    },
    minDate() {
      return dayjs(
        dayjs()
          .local()
          .add(2, 'days')
          .format('YYYY-MM-DD')
      ).toDate();
    },
    unselectableDaysOfWeek() {
      const dayOfWeekAvailability = _.get(this.support.dayOfWeekAvailability, this.property_id);

      if (dayOfWeekAvailability) {
        const unselectable = [];
        _.forEach(dayOfWeekAvailability, (available, key) => {
          if (!available) {
            unselectable.push(DayToNumberMap[key]);
          }
        });
        return unselectable;
      } else {
        return [DayToNumberMap['saturday'], DayToNumberMap['sunday']];
      }
    },
  },
  async created() {
    await this[PROPERTIES__GET_PROPERTIES_AND_SCHEDULES]();

    if (!_.size(this.streetOptionsArray)) {
      lsToastService.open({
        message: this.t('noMowingSchedules'),
        type: 'is-error',
      });
      this.$router.push({
        name: 'support',
      });
      return;
    }

    this.property_id = this.streetOptionsArray[0].value;
    this[SUPPORT__DAY_OF_WEEK_AVAILABILITY]({ property_id: this.property_id });

    this.reason = 'event';
    this.reason_details = '';
    this.requested_is_contractor_flexible = false;
  },
  methods: {
    ...mapActions([
      PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
      SUPPORT__DAY_OF_WEEK_AVAILABILITY,
      SUPPORT__ONE_TIME_RESCHEDULE,
    ]),
    async requestOTSDC() {
      const valid = await this.$validator.validateAll();
      if (!valid) {
        return lsValidationService.scrollToInvalid();
      }

      this[SUPPORT__ONE_TIME_RESCHEDULE]({
        property_id: this.property_id,
        schedule_id: this.selectedProperty.active_mowing_schedule_id,
        date: dayjs(this.date).format('YYYY-MM-DD'),
        reason: this.reason,
        reason_details: this.reason_details,
        requested_is_contractor_flexible: this.requested_is_contractor_flexible,
        onSuccess: this.onSuccess,
      });
    },
    onSuccess() {
      this.$router.push({ name: 'support' });
    },
    reasonChanged() {
      this.reason_details = '';
    },
    propertyChanged(property_id) {
      this[SUPPORT__DAY_OF_WEEK_AVAILABILITY]({ property_id });
      this.date = null;
      this.$validator.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
form > :not(:last-child) {
  margin-bottom: 20px !important;
}

p {
  font-style: italic;
}

.submit-button {
  min-width: 284px;
}
</style>
